import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { UiAtomButtonComponent } from '../../atoms/ui-atom-button/ui-atom-button.component';
import { UiMoleculeInputComponent } from '../ui-molecule-input/ui-molecule-input.component';
import { UiAtomTextComponent } from '../../atoms/ui-atom-text/ui-atom-text.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { SessionService } from '@core/services/use-cases/session.service';
import {
  UserService,
  UserMePasswordChange,
  UserMeUpdate,
} from '@core/services/use-cases/user.service';

export interface UserProfileData {
  title: string;
  message?: string;
}

export interface UserProfileResult {
  success: boolean;
}

@Component({
  selector: 'app-ui-molecule-dialog-user-profile',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    UiAtomButtonComponent,
    UiMoleculeInputComponent,
    UiAtomTextComponent,
    TranslatePipe,
  ],
  templateUrl: './ui-molecule-dialog-user-profile.component.html',
  styleUrl: './ui-molecule-dialog-user-profile.component.scss',
})
export class UiMoleculeDialogUserProfileComponent {
  profileForm: FormGroup;
  isLoading = false;
  hideCurrentPassword = true;
  hideNewPassword = true;
  hideConfirmPassword = true;
  currentUser: any = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly sessionService: SessionService,
    private readonly userService: UserService,
    private readonly snackbar: MatSnackBar,
    private readonly translate: TranslateService,
    private readonly dialogRef: MatDialogRef<
      UiMoleculeDialogUserProfileComponent,
      UserProfileResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: UserProfileData,
  ) {
    this.currentUser = this.sessionService.getCurrentUser();

    this.profileForm = this.fb.group(
      {
        // Información del usuario (editable)
        firstName: [
          this.currentUser?.firstName || '',
          [Validators.required, Validators.minLength(1), Validators.maxLength(50)],
        ],
        lastName: [
          this.currentUser?.lastName || '',
          [Validators.required, Validators.minLength(1), Validators.maxLength(100)],
        ],
        email: [
          this.currentUser?.email || '',
          [
            Validators.required,
            Validators.email,
            Validators.minLength(6),
            Validators.maxLength(320),
          ],
        ],
        role: [{ value: this.currentUser?.role || '', disabled: true }],

        // Cambio de contraseña
        currentPassword: [''],
        newPassword: [''],
        confirmPassword: [''],
      },
      {
        validators: this.passwordMatchValidator,
      },
    );
  }

  private passwordMatchValidator(form: FormGroup): { [key: string]: boolean } | null {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      return { passwordMismatch: true };
    }

    return null;
  }

  onSubmit(): void {
    console.log('🔍 Form submitted', {
      valid: this.profileForm.valid,
      value: this.profileForm.value,
      errors: this.profileForm.errors,
    });

    if (this.profileForm.valid) {
      this.isLoading = true;

      const { firstName, lastName, email, currentPassword, newPassword, confirmPassword } =
        this.profileForm.value;

      // Verificar si se quiere cambiar la contraseña
      const wantsToChangePassword = newPassword && newPassword.trim() !== '';

      if (wantsToChangePassword) {
        // Validaciones manuales para la contraseña
        if (newPassword.length < 12 || newPassword.length > 128) {
          this.snackbar.open(
            this.translate.instant('userProfile.errors.passwordMinLength')
          );
          this.isLoading = false;
          return;
        }

        if (!currentPassword || currentPassword.trim() === '') {
          this.snackbar.open(
            this.translate.instant('userProfile.errors.currentPasswordRequired')
          );
          this.isLoading = false;
          return;
        }

        if (newPassword !== confirmPassword) {
          this.snackbar.open(this.translate.instant('userProfile.errors.passwordMismatch'));
          this.isLoading = false;
          return;
        }

        // Cambiar contraseña del propio usuario
        const passwordChange: UserMePasswordChange = {
          currentPassword,
          newPassword,
        };

        this.userService.changeMePassword(passwordChange).subscribe({
          next: () => {
            // Después de cambiar la contraseña, actualizar los datos del usuario
            this.updateUserData(firstName, lastName, email);
          },
          error: (error) => {
            this.snackbar.open(
              this.translate.instant('userProfile.messages.passwordChangeError')
            );
            this.isLoading = false;
          },
        });
      } else {
        // Solo actualizar datos del usuario sin cambiar contraseña
        this.updateUserData(firstName, lastName, email);
      }
    }
  }

  private updateUserData(firstName: string, lastName: string, email: string): void {
    const userData: UserMeUpdate = {
      firstName,
      lastName,
      email,
    };

    console.log('📤 Calling updateMe with:', userData);

    this.userService.updateMe(userData).subscribe({
      next: () => {
        console.log('✅ Update successful');
        // Actualizar datos en el SessionService
        this.sessionService.saveUser({
          ...this.currentUser,
          firstName,
          lastName,
          email,
        });

        this.snackbar.open(this.translate.instant('userProfile.messages.profileUpdated'));

        // Limpiar campos de contraseña
        this.profileForm.patchValue({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });

        this.isLoading = false;
        this.dialogRef.close({ success: true });
      },
      error: (error) => {
        console.error('❌ Update error:', error);
        this.snackbar.open(
          this.translate.instant('userProfile.messages.profileUpdateError')
        );
        this.isLoading = false;
      },
    });
  }

  togglePasswordVisibility(field: 'current' | 'new' | 'confirm'): void {
    switch (field) {
      case 'current':
        this.hideCurrentPassword = !this.hideCurrentPassword;
        break;
      case 'new':
        this.hideNewPassword = !this.hideNewPassword;
        break;
      case 'confirm':
        this.hideConfirmPassword = !this.hideConfirmPassword;
        break;
    }
  }

  get currentPasswordControl() {
    return this.profileForm.get('currentPassword');
  }

  get newPasswordControl() {
    return this.profileForm.get('newPassword');
  }

  get confirmPasswordControl() {
    return this.profileForm.get('confirmPassword');
  }

  get passwordMismatchError() {
    return this.profileForm.hasError('passwordMismatch');
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }
}
